import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import type { ReactNode } from 'react';
import { cva, cx, type VariantProps } from '@/cva.config';

const tabListVariants = cva({
  base: '-mb-px flex',
  defaultVariants: { layout: 'inline' },
  variants: {
    layout: {
      inline: 'items-center gap-1',
      stretch: 'items-stretch gap-0 self-stretch',
    },
  },
});

const tabTriggerVariants = cva({
  base: 'inline-flex items-center gap-1.5 border-b-2 font-medium transition-colors',
  defaultVariants: { active: false, layout: 'inline' },
  variants: {
    active: {
      false:
        'border-transparent text-foreground-muted hover:border-border-active hover:text-foreground',
      true: 'border-accent text-foreground',
    },
    layout: {
      inline: 'px-3 py-2 text-xs lg:text-sm',
      stretch: 'self-stretch px-3 text-xs lg:text-sm',
    },
  },
});

export interface TabItem<T extends string> {
  badge?: ReactNode;
  id: T;
  label: ReactNode;
  visible?: boolean;
}

interface TabsProps<T extends string>
  extends VariantProps<typeof tabListVariants> {
  className?: string;
  items: TabItem<T>[];
  onChange: (value: T) => void;
  renderTrigger?: (props: {
    active: boolean;
    item: TabItem<T>;
    trigger: ReactNode;
  }) => ReactNode;
  value: T | null | undefined;
}

export const Tabs = <T extends string>({
  className,
  items,
  layout,
  onChange,
  renderTrigger,
  value,
}: TabsProps<T>) => (
  <BaseTabs.Root
    className={className}
    onValueChange={(next) => onChange(next as T)}
    value={value ?? null}
  >
    <BaseTabs.List className={tabListVariants({ layout })}>
      {items
        .filter((item) => item.visible !== false)
        .map((item) => {
          const active = item.id === value;
          const trigger = (
            <BaseTabs.Tab
              className={cx(tabTriggerVariants({ active, layout }))}
              key={item.id}
              value={item.id}
            >
              {item.label}
              {item.badge}
            </BaseTabs.Tab>
          );

          return renderTrigger ? (
            <span key={item.id}>
              {renderTrigger({ active, item, trigger })}
            </span>
          ) : (
            <span key={item.id}>{trigger}</span>
          );
        })}
    </BaseTabs.List>
  </BaseTabs.Root>
);
